import { useCallback, useState } from "react";
import { useSelector } from "react-redux";
import { useFormik } from "formik";
import * as Yup from "yup";

import { apiClient } from "src/api/client";
import closeIcon from "src/assets/icons/closeGray.svg";
import { Button, BUTTONTYPE } from "src/components/atoms/Button";
import { ErrorBlock } from "src/components/atoms/ErrorBlock";
import { Input } from "src/components/atoms/Input";
import { Select } from "src/components/atoms/Select";
import useIndustries from "src/hooks/useIndustries";
import { useAppDispatch } from "src/store/hooks";
import { fetchClientDetail } from "src/store/slices/clientDetailSlice";
import { closeModal } from "src/store/slices/modalSlice";
import { RootState } from "src/store/store";
import { getErrorMessage } from "src/utils/errors/getErrorMessage";

import styles from "./styles.module.css";

type Option = {
  id: string | number;
  label: string;
};

const validationSchema = Yup.object({
  companyName: Yup.string()
    .required("Company name is required")
    .min(2, "Company name must be at least 2 characters"),
  industry: Yup.object().required("Industry is required"),
});

export const AddCompanyModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    industries,
    loading,
    error: loadError,
  } = useIndustries();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const modalData = useSelector(
    (state: RootState) =>
      (state.modal.data["addCompanyModal"] as { clientId?: number }) || {},
  );
  const clientId = modalData.clientId;
  const industryOptions = industries.map((industry) => ({
    id: industry.id,
    label: industry.name,
  }));

  const formik = useFormik({
    initialValues: {
      companyName: "",
      industry: null as Option | null,
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      setSubmitError(null);
      try {
        await apiClient.createCompany({
          name: values.companyName.trim(),
          type_id: null,
          industry_id: Number(values.industry?.id),
          state_id: null,
          user_id: clientId || 0,
        });
        if (clientId) {
          dispatch(fetchClientDetail(clientId));
        }
        window.dispatchEvent(new CustomEvent("companyCreated"));
        dispatch(closeModal({ type: "addCompanyModal" }));
      } catch (error: unknown) {
        setSubmitError(getErrorMessage(error));
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  const industryHandler = useCallback(
    (id: string | number | null) => {
      const selected = industryOptions.find((item) => item.id === id);
      if (selected) {
        formik.setFieldValue("industry", selected);
      }
    },
    [formik, industryOptions],
  );

  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <h3>Add a company</h3>
          <Button
            text=""
            icon={closeIcon}
            btnType={BUTTONTYPE.WITHOUT_BORDER}
            onClick={() =>
              dispatch(closeModal({ type: "addCompanyModal" }))
            }
          />
        </div>
        {loading ? (
          <div className={styles.loading}>Loading...</div>
        ) : loadError ? (
          <ErrorBlock error={loadError} />
        ) : (
          <form onSubmit={formik.handleSubmit} className={styles.form}>
            <Input
              value={formik.values.companyName}
              label="Company name"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              name="companyName"
              error={
                formik.touched.companyName && formik.errors.companyName
              }
            />
            <Select
              label="Industry"
              items={industryOptions}
              onSelect={industryHandler}
              selectedId={formik.values.industry?.id}
              className={styles.relative}
            />
            <ErrorBlock error={submitError} />
            <Button
              text={isSubmitting ? "Adding..." : "Add company"}
              type="submit"
              disabled={!formik.isValid || !formik.dirty || isSubmitting}
            />
          </form>
        )}
      </div>
    </div>
  );
};
